import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

// '../../' is the path to src/
import config from '../../config';

export function LoginPage() {

    const navigate = useNavigate();     
    const [input, setInput] = useState({
        username: '', 
        password: ''        
    });    
    const [error, setError] = useState( {} );     
    const [loggingIn, setLoggingIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    function handleChange(e) {
        const { name, value } = e.target;                
        setInput(prev => ({
            ...prev,
            [name]: value
        }));
        validateInput(e);
    }

    const validateInput = e => {
        let { name, value } = e.target;
        setError(prev => {
            const stateObj = {  ...prev, [name]: "" };
            delete stateObj[name]; // helps us to determine if there are any errors
            return { ...stateObj ,..._validateInput( name, value ) }            
        });        
    }

    function handleSubmit(e) {
        e.preventDefault();

        if( !_isFormValid() ) return;

        setLoggingIn(true);

        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(input)
        };

        fetch(`${config.apiUrl}/api/users/authenticate`, requestOptions )
            .then( handleResponse )
            .then(
                user => {
                    setLoggingIn(false);

                    // Check if MFA is required
                    if (user.mfaRequired) {
                        // Redirect to MFA verification page with user data
                        navigate('/mfa-verify', { state: { userData: user } });
                        return;
                    }

                    sessionStorage.setItem( 'user', JSON.stringify(user) );
                    window.dispatchEvent( new Event('storage') ); // dispath a local storage change notification

                    let destinationUri = '/profile';

                    if(user.connections) {
                        const sources = new Set( user.connections.split(",") );
                        if( sources.has('source') && sources.has('target') ) destinationUri = '/profile/dashboard';
                        else if( sources.has('source') ) destinationUri = '/profile/configureSource';
                        else if( sources.has('target') ) destinationUri = '/profile/configureTarget';
                    }

                    navigate( destinationUri );
                },
                error => { 
                    setErrorMessage( error ); 
                    setLoggingIn(false); 
                    
                    // If account is locked, show additional help message
                    if (error && error.includes('locked')) {
                        setErrorMessage(error + ' If you haven\'t received an unlock email, please contact support.');
                    }
                }
            );
    }

    function _isFormValid() {        
        let combinedState = {};

        for (let key in input) {                        
            combinedState = { ...combinedState, ..._validateInput( key, input[key] ) };
        }       

        setError(prev => {
            return {  ...prev, ...combinedState }
        });

        return Object.keys(combinedState).length === 0;
    }

    function _validateInput( name, value ) {
        const stateObj = {};

        switch (name) {
  
            case "username":
              if (!value) {                
                stateObj[name] = "Please enter Username.";
              }
              break;
     
            case "password":
              if (!value) { 
                stateObj[name] = "Please enter Password.";
              }                
              break;

            default:
              break;
        }
        return stateObj;
    }

    function handleResponse(response) {

        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if (!response.ok) {
                if (response.status === 401) {
                    // auto logout if 401 response returned from api
                    //logout();
                    //window.location.reload(true);
                }
    
                const error = (data && data.message) || response.statusText;
                return Promise.reject(error);
            }
           
            return data;
        });
    }

    return (
        <div className="min-vh-100 d-flex align-items-center" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
            <div className="container">
                <div className="row justify-content-center">
                    <div className="col-md-6 col-lg-4">
                        <div className="modern-card text-center mb-4">
                            <div className="card-body p-5">
                                <div className="mb-4">
                                    <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                         style={{width: '4rem', height: '4rem', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                                        <i className="bi bi-person-fill text-white fs-3"></i>
                                    </div>
                                    <h2 className="fw-bold mb-2" style={{color: '#1e293b'}}>Welcome Back</h2>
                                    <p className="text-muted mb-0">Sign in to your account to continue</p>
                                </div>

                                {errorMessage && (
                                    <div className="alert alert-danger rounded-3 mb-4" role="alert">
                                        <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                        {errorMessage}
                                    </div>
                                )}

                                <form name="form" onSubmit={handleSubmit}>
                                    <div className={'mb-3' + (error.username ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className={'form-control ' + (error.username ? 'is-invalid' : '')}
                                                id="username"
                                                name="username"
                                                value={input.username}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="username"
                                                placeholder="Username"
                                            />
                                            <label htmlFor="username">Username</label>
                                            {error.username && <div className='invalid-feedback'>{error.username}</div>}
                                        </div>
                                    </div>

                                    <div className={'mb-4' + (error.password ? ' has-error' : '')}>
                                        <div className="form-floating">
                                            <input
                                                type="password"
                                                className={'form-control ' + (error.password ? 'is-invalid' : '')}
                                                id="password"
                                                name="password"
                                                value={input.password}
                                                onChange={handleChange}
                                                onBlur={validateInput}
                                                autoComplete="current-password"
                                                placeholder="Password"
                                            />
                                            <label htmlFor="password">Password</label>
                                            {error.password && <div className='invalid-feedback'>{error.password}</div>}
                                        </div>
                                    </div>

                                    <div className="d-grid gap-2 mb-4">
                                        <button
                                            type="submit"
                                            className="btn btn-modern btn-lg"
                                            disabled={loggingIn}
                                        >
                                            {loggingIn ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Signing In...
                                                </>
                                            ) : (
                                                'Sign In'
                                            )}
                                        </button>
                                    </div>

                                    <div className="d-flex justify-content-between gap-2">
                                        <Link to="/registration" className="btn btn-outline-modern flex-fill">
                                            Create Account
                                        </Link>
                                        <Link to="/forgot-password" className="btn btn-link text-muted">
                                            Forgot Password?
                                        </Link>
                                    </div>
                                </form>
                            </div>
                        </div>

                        <div className="text-center">
                            <p className="text-white-50 small mb-0">
                                <i className="bi bi-shield-check me-1"></i>
                                Your information is secure and encrypted
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}